import FieldEditingIndicator from 'components/FieldEditingIndicator/FieldEditingIndicator';
import { useDateFormatter } from 'hooks/useDateFormatter';
import { usePpsAccess } from 'hooks/usePpsAccess';
import { Job } from 'Interfaces/JobsInterface';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Column } from 'primereact/column';
import { DataTable, DataTableRowReorderEvent } from 'primereact/datatable';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { JobPasteStore } from '../../../../stores/JobPasteStore';
import { commaSeparatedNumbers } from '../../../../utils/commaSeparatedNumbers';
import NoteTemplateWithIcon from './components/NoteTemplateWithIcon/NoteTemplateWithIcon';
import RoomHeaderTemplate from './components/RoomHeaderTemplate';
import classes from './RoomAccordion.module.scss';
import { NumberOptions, Options, useRoomAccordion } from './useRoomAccordion';

interface INumberEditor {
  field: keyof Job;
  jobId: string;
  roomId: string;
  JobPasteStore: JobPasteStore;
  disabled?: boolean;
}

interface IDateEditor {
  field: keyof Job;
  jobId: string;
  roomId: string;
  JobPasteStore: JobPasteStore;
  showTime: boolean;
  minDateValidation?: boolean;
  disabled?: boolean;
  fieldType?: string;
}

interface ITextEditor {
  field: keyof Job;
  jobId: string;
  roomId: string;
  JobPasteStore: JobPasteStore;
  disabled?: boolean;
}

interface IDropdownEditor {
  field: keyof Job;
  jobId: string;
  roomId: string;
  dropdownOptions: Options;
  dropdownLoading: boolean;
  JobPasteStore: JobPasteStore;
  disabled?: boolean;
}

// Helper function to check if a job should be disabled based on its status
const isJobDisabledByStatus = (job: Job): boolean => {
  const status = job.status?.toLowerCase();
  return status === 'complete and remove';
};

// Factory function to create editable column body renderer
const createEditableColumnBodyRenderer =
  (
    field: keyof Job,
    roomId: string,
    isEditable: boolean,
    onlyFirstRowEditable: boolean,
    dataType: string,
    optionsLoading: boolean | undefined,
    options: Options | NumberOptions | null,
    showTime: boolean,
    minDateValidation: boolean,
    JobPasteStore: JobPasteStore,
    allStatusOptions: Options,
    isFieldDisabled: (field: keyof Job, isEditMode: boolean) => boolean,
    formatDate: (date: Date) => string,
    commaSeparatedNumbers: (value: any) => string,
    TextEditor: React.FC<ITextEditor>,
    NumberEditor: React.FC<INumberEditor>,
    DropdownEditor: React.FC<IDropdownEditor>,
    DateEditor: React.FC<IDateEditor>,
    _globalIsEditMode: boolean,
    department: string | undefined
  ) =>
  (rowData: Job, _rowInfo: any) => {
    // Helper function to check if user is from restricted departments that can only edit status
    const isUserFromRestrictedDepartment = (userDepartment: string | undefined): boolean => {
      const restrictedDepartments = ['stager', 'qls', 'qa', 'mo', 'pa'];
      return userDepartment ? restrictedDepartments.includes(userDepartment.toLowerCase()) : false;
    };

    // Helper function to get the current job position within its room (respecting reordering)
    const getCurrentJobPositionInRoom = (job: Job, roomId: string): number => {
      // Get the actual room order (ignoring search filters) to determine which job is "first"
      let actualRoomJobs: Job[] = [];

      if (JobPasteStore.isEditMode && JobPasteStore._cloned_jobs_for_edits) {
        // In edit mode, use cloned jobs (which respect reordering)
        const clonedRoom = JobPasteStore._cloned_jobs_for_edits.find(room => room.id === roomId);
        actualRoomJobs = clonedRoom ? clonedRoom.jobs : [];
      } else {
        // Not in edit mode, use original jobs
        const originalRoom = JobPasteStore.jobs?.find(room => room.id === roomId);
        actualRoomJobs = originalRoom ? originalRoom.jobs : [];
      }

      if (!actualRoomJobs || actualRoomJobs.length === 0) {
        return 0;
      }

      // Find the job's position in the actual room order
      const jobIndex = actualRoomJobs.findIndex(j => j.jobId === job.jobId);
      return jobIndex >= 0 ? jobIndex : 0;
    };

    // Get the current job position in its room (respecting reordering)
    const currentJobPosition = getCurrentJobPositionInRoom(rowData, roomId);
    const isFirstRow = currentJobPosition === 0;

    // Special case for clean override column - only editable for non-first rows in edit mode
    if (field === 'cleanOverride') {
      if (isFirstRow) {
        return <>-</>;
      }

      // Check if user is from restricted departments that can only edit status
      const isRestrictedDepartment = isUserFromRestrictedDepartment(department);

      // For non-first rows, show dropdown editor if this specific room is in edit mode and user is not from restricted department
      if (isEditable && !isRestrictedDepartment) {
        return (
          <DropdownEditor
            jobId={rowData.jobId}
            field={field}
            roomId={roomId}
            JobPasteStore={JobPasteStore}
            dropdownLoading={optionsLoading || false}
            dropdownOptions={options as Options}
            disabled={isJobDisabledByStatus(rowData)}
          />
        );
      } else {
        // Not in edit mode or restricted department, show the value as text only
        const value = rowData[field] as string | number | Date | null;
        if (value !== null && value !== undefined && value !== '') {
          // For cleanOverride, display in uppercase
          return <>{String(value).toUpperCase()}</>;
        } else {
          return <>-</>;
        }
      }
    }

    // Special case for job start time column - only editable for first row in edit mode
    if (field === 'jobStartTime') {
      if (!isFirstRow) {
        return <>-</>;
      }

      // Check if user is from restricted departments that can only edit status
      const isRestrictedDepartment = isUserFromRestrictedDepartment(department);

      // For first row, show date editor if this specific room is in edit mode and user is not from restricted department
      if (isEditable && !isRestrictedDepartment) {
        return (
          <DateEditor
            showTime={showTime}
            jobId={rowData.jobId}
            field={field}
            roomId={roomId}
            JobPasteStore={JobPasteStore}
            minDateValidation={minDateValidation}
            disabled={isJobDisabledByStatus(rowData)}
            fieldType="dateTime"
          />
        );
      } else {
        // Not in edit mode or restricted department, show the value
        const value = rowData[field] as string | number | Date | null;
        if (value instanceof Date) {
          return <>{formatDate(value)}</>;
        } else if (value !== null && value !== undefined && value !== '') {
          return <>{commaSeparatedNumbers(value)}</>;
        } else {
          return <>-</>;
        }
      }
    }

    // For all other fields, use normal logic
    const shouldBeEditable = isEditable && (!onlyFirstRowEditable || isFirstRow);

    // Pass true for isEditMode since we're in edit mode here
    const isDisabledByRBAC = isFieldDisabled(field, true);

    // Check if user is from restricted departments that can only edit status
    const isRestrictedDepartment = isUserFromRestrictedDepartment(department);
    const isFieldRestrictedForDepartment = isRestrictedDepartment && field !== 'status';

    // If not in edit mode, field is disabled by RBAC, or field is restricted for this department, just display value
    if (!shouldBeEditable || isDisabledByRBAC || isFieldRestrictedForDepartment) {
      const value = rowData[field] as string | number | Date | null;
      if (value instanceof Date) {
        return <>{formatDate(value)}</>;
      } else if (value !== null && value !== undefined && value !== '') {
        // For cleanOverride, display in uppercase
        if (String(field) === 'cleanOverride') {
          return <>{String(value).toUpperCase()}</>;
        }
        return <>{commaSeparatedNumbers(value)}</>;
      } else {
        return <>-</>;
      }
    }

    // For status field, get options from getStatusOptionsForJob
    // Status field should always be editable to allow changing back from "complete and remove"
    if (field === 'status' && dataType === 'dropdown') {
      const statusOptions = allStatusOptions;

      return (
        <DropdownEditor
          jobId={rowData.jobId}
          field={field}
          roomId={roomId}
          JobPasteStore={JobPasteStore}
          dropdownLoading={optionsLoading || false}
          dropdownOptions={statusOptions}
          disabled={false} // Status field is always editable
        />
      );
    }

    // Only render edit controls if editable
    switch (dataType) {
      case 'number':
        return (
          <NumberEditor
            jobId={rowData.jobId}
            field={field}
            roomId={roomId}
            JobPasteStore={JobPasteStore}
            disabled={isJobDisabledByStatus(rowData)}
          />
        );
      case 'dropdown':
        return (
          <DropdownEditor
            jobId={rowData.jobId}
            field={field}
            roomId={roomId}
            JobPasteStore={JobPasteStore}
            dropdownLoading={optionsLoading ? optionsLoading : false}
            dropdownOptions={options as Options}
            disabled={isJobDisabledByStatus(rowData)}
          />
        );
      case 'dateTime':
        return (
          <DateEditor
            showTime={showTime}
            jobId={rowData.jobId}
            field={field}
            roomId={roomId}
            JobPasteStore={JobPasteStore}
            minDateValidation={minDateValidation}
            disabled={isJobDisabledByStatus(rowData)}
            fieldType="dateTime"
          />
        );
      default:
        return (
          <TextEditor
            jobId={rowData.jobId}
            field={field}
            roomId={roomId}
            JobPasteStore={JobPasteStore}
            disabled={isJobDisabledByStatus(rowData)}
          />
        );
    }
  };

// Empty message for DataTable when no jobs are present
const EmptyJobs = () => {
  return (
    <div className={classes.emptyJobs}>
      <p>No jobs available for this room</p>
    </div>
  );
};

// Empty message when no search results are found
const NoSearchResults = () => {
  return (
    <div className={classes.emptyJobs}>
      <p>No jobs match your search criteria</p>
    </div>
  );
};

interface RoomAccordionProps {
  onEditClick?: (roomId: string) => void;
  onCancelClick?: () => void;
  onSaveClick?: (roomId: string) => void;
}

const RoomAccordion: React.FC<RoomAccordionProps> = ({
  onEditClick: propOnEditClick,
  onCancelClick: propOnCancelClick,
  onSaveClick: propOnSaveClick
}) => {
  const {
    JobPasteStore,
    onEditClick: hookOnEditClick,
    onCancelClick: hookOnCancelClick,
    isEditMode,
    roomOptions,
    statusOptions: allStatusOptions,
    onFieldChange,
    clearnOverrideOptions,
    priotityOptions,
    onEditedRoomSave,
    cleanTypeIsLoading,
    roomOptionLoading,
    statusOptionsLoading,
    onRowReorder,
    isRBACAllowed,
    department
  } = useRoomAccordion();

  // Use prop functions if provided, otherwise use hook functions
  const handleEditClick = propOnEditClick || hookOnEditClick;
  const handleCancelClick = propOnCancelClick || hookOnCancelClick;
  const handleSaveClick = propOnSaveClick || onEditedRoomSave;
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const { isFieldDisabled } = usePpsAccess();

  // Save changes handler
  const onSaveClick = (roomId: string) => {
    handleSaveClick(roomId);
  };

  const { formatDate } = useDateFormatter();

  const TextEditor = React.memo(
    observer(({ field, jobId, roomId, JobPasteStore, disabled }: ITextEditor) => {
      let value = '';
      if (JobPasteStore._cloned_jobs_for_edits) {
        try {
          const fieldValue = JobPasteStore._cloned_jobs_for_edits
            .find(item => item.id === roomId)!
            .jobs.find(job => job.jobId === jobId)![field];
          value = String(fieldValue || '');
        } catch (error) {
          if (environment === 'development') {
            console.error('Error accessing data in TextEditor:', error);
          }
          toast.error('Error accessing data in TextEditor');
        }
      }

      // Get room name for field editing indicator
      const roomName =
        JobPasteStore._cloned_jobs_for_edits?.find(item => item.id === roomId)?.name ||
        'Unknown Room';

      return (
        <FieldEditingIndicator roomName={roomName} jobId={jobId} fieldName={field} fieldType="text">
          <InputText
            type="text"
            value={value}
            onChange={e => onFieldChange(jobId, roomId, field, e.target.value)}
            disabled={disabled}
          />
        </FieldEditingIndicator>
      );
    })
  );

  const NumberEditor = React.memo(
    observer(({ field, jobId, roomId, JobPasteStore, disabled }: INumberEditor) => {
      if (!JobPasteStore._cloned_jobs_for_edits) {
        toast.error('Room not in edit mode');
        return null; // Return null to prevent rendering if not in edit mode
      }

      const currentCellValue = JobPasteStore._cloned_jobs_for_edits
        .filter(item => item.id === roomId)[0]
        .jobs.filter(job => job.jobId === jobId)[0][field];

      // Get room name for field editing indicator
      const roomName =
        JobPasteStore._cloned_jobs_for_edits?.find(item => item.id === roomId)?.name ||
        'Unknown Room';

      return (
        <FieldEditingIndicator
          roomName={roomName}
          jobId={jobId}
          fieldName={field}
          fieldType="number"
        >
          <InputNumber
            value={currentCellValue as number}
            onChange={e => onFieldChange(jobId, roomId, field, e.value as number)}
            min={0}
            disabled={disabled}
          />
        </FieldEditingIndicator>
      );
    })
  );

  const DropdownEditor = React.memo(
    observer(
      ({
        field,
        jobId,
        roomId,
        JobPasteStore,
        dropdownOptions,
        dropdownLoading,
        disabled
      }: IDropdownEditor) => {
        let value = ''; // Initialize as empty string

        if (JobPasteStore._cloned_jobs_for_edits) {
          try {
            const fieldValue = JobPasteStore._cloned_jobs_for_edits
              .find(item => item.id === roomId)!
              .jobs.find(job => job.jobId === jobId)![field];
            // Only set value if there's an actual value
            if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
              value = String(fieldValue);
            }
          } catch (error) {
            if (environment === 'development') {
              console.error('Error accessing data in DropdownEditor:', error);
            }
            toast.error('Error accessing data in DropdownEditor');
          }
        }

        // For status field, default to "ready" if no value is set
        // For other fields (like cleanOverride), keep empty to show "Select" placeholder
        if (!value && field === 'status' && dropdownOptions && dropdownOptions.length > 0) {
          // Find "ready" option, otherwise use first option
          const readyOption = dropdownOptions.find(
            option =>
              option.value.toLowerCase() === 'ready' || option.label.toLowerCase() === 'ready'
          );
          value = readyOption ? readyOption.value : dropdownOptions[0].value;
        }
        // Get room name for field editing indicator
        const roomName =
          JobPasteStore._cloned_jobs_for_edits?.find(item => item.id === roomId)?.name ||
          'Unknown Room';

        return (
          <FieldEditingIndicator
            roomName={roomName}
            jobId={jobId}
            fieldName={field}
            fieldType="dropdown"
          >
            <select
              value={value}
              onChange={e => onFieldChange(jobId, roomId, field, e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                minWidth: '120px', // Increased minimum width
                padding: '8px 12px', // More padding like PrimeReact
                border: '1px solid #ced4da', // PrimeReact border color
                borderRadius: '6px', // PrimeReact border radius
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: disabled ? '#f8f9fa' : 'white',
                color: disabled ? '#6c757d' : '#495057', // PrimeReact text colors
                cursor: disabled ? 'not-allowed' : 'pointer',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                appearance: 'none', // Remove default arrow
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                backgroundSize: '16px',
                paddingRight: '32px' // Make room for custom arrow
              }}
              onFocus={e => {
                e.target.style.borderColor = '#80bdff';
                e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#ced4da';
                e.target.style.boxShadow = 'none';
              }}
            >
              {/* Only show "Select" option for specific fields (exclude priority and room) */}
              {field !== 'status' && field !== 'priority' && field !== 'room' && (
                <option value="">Select</option>
              )}
              {dropdownOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FieldEditingIndicator>
        );
      }
    )
  );

  // Helper function to convert cell value to Date or null
  const getDateValue = (currentCellValue: string | Date | null): Date | null => {
    if (!currentCellValue) {
      return null;
    }

    if (currentCellValue instanceof Date) {
      return currentCellValue;
    }

    return new Date(currentCellValue);
  };

  const DateEditor = React.memo(
    observer(
      ({
        field,
        jobId,
        roomId,
        JobPasteStore,
        showTime = false,
        minDateValidation = false,
        disabled,
        fieldType = 'date'
      }: IDateEditor) => {
        if (!JobPasteStore._cloned_jobs_for_edits) {
          toast.error('Room not in edit mode');
          return null; // Return null to prevent rendering if not in edit mode
        }

        const currentCellValue = JobPasteStore._cloned_jobs_for_edits
          .filter(item => item.id === roomId)[0]
          .jobs.filter(job => job.jobId === jobId)[0][field] as string | Date | null;

        // Get room name for field editing indicator
        const roomName =
          JobPasteStore._cloned_jobs_for_edits?.find(item => item.id === roomId)?.name ||
          'Unknown Room';

        return (
          <FieldEditingIndicator
            roomName={roomName}
            jobId={jobId}
            fieldName={field}
            fieldType={fieldType}
          >
            <input
              type={showTime ? 'datetime-local' : 'date'}
              value={(() => {
                const dateValue = getDateValue(currentCellValue);
                if (!dateValue) return '';

                // Convert to the format expected by datetime-local input (YYYY-MM-DDTHH:MM)
                const year = dateValue.getFullYear();
                const month = String(dateValue.getMonth() + 1).padStart(2, '0');
                const day = String(dateValue.getDate()).padStart(2, '0');

                if (showTime) {
                  const hours = String(dateValue.getHours()).padStart(2, '0');
                  const minutes = String(dateValue.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                } else {
                  return `${year}-${month}-${day}`;
                }
              })()}
              onChange={e => {
                const value = e.target.value;
                if (!value) {
                  onFieldChange(jobId, roomId, field, null as any);
                  return;
                }

                // Convert from input format back to Date object
                const date = new Date(value);
                onFieldChange(jobId, roomId, field, date);
              }}
              min={minDateValidation ? new Date().toISOString().slice(0, 16) : undefined}
              disabled={disabled}
              style={{
                width: '100%',
                minWidth: '120px',
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: disabled ? '#f8f9fa' : 'white',
                color: disabled ? '#6c757d' : '#495057',
                cursor: disabled ? 'not-allowed' : 'pointer',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={e => {
                e.target.style.borderColor = '#80bdff';
                e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#ced4da';
                e.target.style.boxShadow = 'none';
              }}
            />
          </FieldEditingIndicator>
        );
      }
    )
  );

  // Modified function that checks if it's the first row for certain fields
  const getEditableColumnTemplate = useCallback(
    (
      field: keyof Job,
      roomId: string,
      optionsLoading?: boolean,
      options: Options | NumberOptions | null = null,
      dataType = 'text',
      onlyFirstRowEditable = false,
      showTime = false,
      minDateValidation = false
    ) => {
      const isEditable = isEditMode && JobPasteStore.currentRoomInEditMode === roomId;

      return createEditableColumnBodyRenderer(
        field,
        roomId,
        isEditable,
        onlyFirstRowEditable,
        dataType,
        optionsLoading,
        options,
        showTime,
        minDateValidation,
        JobPasteStore,
        allStatusOptions,
        isFieldDisabled,
        formatDate,
        commaSeparatedNumbers,
        TextEditor,
        NumberEditor,
        DropdownEditor,
        DateEditor,
        isEditMode,
        department
      );
    },
    [
      isEditMode,
      JobPasteStore,
      allStatusOptions,
      isFieldDisabled,
      formatDate,
      commaSeparatedNumbers,
      onFieldChange,
      department
    ]
  );

  // Display a sequential number (1, 2, 3...) instead of the actual serial value
  const serialNumberTemplate = (rowData: Job, rowInfo: any) => {
    // Display the row index + 1 to show a sequential order starting from 1
    return <span>{rowInfo.rowIndex + 1}</span>;
  };

  // Function to get conditional column styles based on edit mode
  const getColumnStyles = (roomId: string, baseStyles: React.CSSProperties) => {
    const isRoomInEditMode = isEditMode && JobPasteStore.currentRoomInEditMode === roomId;

    // If in edit mode, return empty styles (no styling applied)
    if (isRoomInEditMode) {
      return {};
    }

    // If not in edit mode, return the base styles
    return baseStyles;
  };

  // Get the correct jobs data to display based on search and edit state
  const getJobsToDisplay = () => {
    // Always use the computed filteredJobs property for proper reactivity
    // This ensures that WebSocket updates trigger re-renders correctly
    return JobPasteStore.filteredJobs;
  };

  // Handle row reorder event with room ID context
  const handleRowReorder = (e: DataTableRowReorderEvent<Job[]>, roomId: string) => {
    onRowReorder(e, roomId);
  };

  // Check if there are no search results
  const hasNoSearchResults =
    JobPasteStore.searchQuery.trim() &&
    (!JobPasteStore.filteredJobs || JobPasteStore.filteredJobs.length === 0);

  if (hasNoSearchResults) {
    return (
      <div className={classes.roomAccordionsContainer}>
        <NoSearchResults />
      </div>
    );
  }

  return (
    <div className={classes.roomAccordionsContainer}>
      <Accordion
        multiple
        activeIndex={JobPasteStore.activeIndexList}
        onTabChange={e => JobPasteStore.setActiveIndex(e.index as number[])}
        className={classes.accordion}
        expandIcon={<ChevronUp color={'#4D2E6D'} size={20} />}
        collapseIcon={<ChevronDown color={'#4D2E6D'} size={20} />}
      >
        {getJobsToDisplay() &&
          getJobsToDisplay()?.map((room, index) => {
            const isExpanded = JobPasteStore.activeIndexList.includes(index);
            const isRoomInEditMode = isEditMode && JobPasteStore.currentRoomInEditMode === room.id;

            return (
              <AccordionTab
                key={room.id}
                headerTemplate={() => (
                  <RoomHeaderTemplate
                    room={room}
                    isExpanded={isExpanded}
                    isEditMode={isEditMode}
                    currentRoomInEditMode={JobPasteStore.currentRoomInEditMode}
                    onCancelClick={handleCancelClick}
                    onEditClick={handleEditClick}
                    onSaveClick={onSaveClick}
                  />
                )}
                className={classes.accordionTab}
              >
                <DataTable
                  value={JobPasteStore.getJobsForRoom(room.id)}
                  emptyMessage={EmptyJobs}
                  className={classes.roomJobsTable}
                  responsiveLayout="scroll"
                  stripedRows
                  showGridlines
                  reorderableRows={isRoomInEditMode}
                  onRowReorder={e => handleRowReorder(e, room.id)}
                  loading={JobPasteStore.isRoomLoading(room.id)}
                  loadingIcon={<ProgressSpinner style={{ width: '4rem', height: '4rem' }} />}
                >
                  {/* Only show reorder handle when in edit mode for this room */}
                  {isRoomInEditMode && isRBACAllowed && (
                    <Column rowReorder style={{ width: '3rem' }} />
                  )}

                  {/* Use serialNumberTemplate to display serial values */}
                  <Column
                    field="serial"
                    header="Sl No."
                    style={getColumnStyles(room.id, {
                      maxWidth: '25px',
                      width: '25px',
                      textAlign: 'left',
                      textWrap: 'wrap',
                      wordWrap: 'break-word'
                    })}
                    body={serialNumberTemplate}
                  />

                  <Column
                    field="jobId"
                    header="Job ID"
                    body={getEditableColumnTemplate('jobId', room.id)}
                    style={getColumnStyles(room.id, {
                      maxWidth: '75px',
                      width: '75px',
                      wordWrap: 'break-word'
                    })}
                  />

                  <Column
                    field="priority"
                    header="Prty."
                    body={getEditableColumnTemplate(
                      'priority',
                      room.id,
                      false,
                      priotityOptions,
                      'dropdown'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '30px',
                      width: '30px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="room"
                    header="Room"
                    body={getEditableColumnTemplate(
                      'room',
                      room.id,
                      roomOptionLoading,
                      roomOptions,
                      'dropdown'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="status"
                    header="Status"
                    body={getEditableColumnTemplate(
                      'status',
                      room.id,
                      statusOptionsLoading,
                      [],
                      'dropdown'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="ndc"
                    header="NDC"
                    body={getEditableColumnTemplate('ndc', room.id)}
                    style={getColumnStyles(room.id, {
                      maxWidth: '75px',
                      width: '75px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="description"
                    header="Description"
                    body={getEditableColumnTemplate('description', room.id)}
                    style={getColumnStyles(room.id, {
                      maxWidth: '150px',
                      width: '150px',
                      textAlign: 'left',
                      textWrap: 'wrap',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="qtyOfContainers"
                    header="Qty of containers to fill"
                    body={rowData =>
                      rowData.qtyOfContainers ? commaSeparatedNumbers(rowData.qtyOfContainers) : '-'
                    }
                    style={getColumnStyles(room.id, {
                      maxWidth: '75px',
                      width: '75px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="qtyPerContainer"
                    header="Qty per Container"
                    body={getEditableColumnTemplate(
                      'qtyPerContainer',
                      room.id,
                      false,
                      null,
                      'number'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '55px',
                      width: '55px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="type"
                    header="Type"
                    body={rowData => rowData.type}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="qtyUnitsToPackage"
                    header="Units to Package"
                    body={getEditableColumnTemplate(
                      'qtyUnitsToPackage',
                      room.id,
                      false,
                      null,
                      'number'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '55px',
                      width: '55px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="confirmedDate"
                    header="Confirmed Date"
                    body={getEditableColumnTemplate(
                      'confirmedDate',
                      room.id,
                      false,
                      null,
                      'dateTime'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '75px',
                      width: '75px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="notes"
                    header="Notes"
                    body={rowData => <NoteTemplateWithIcon {...toJS(rowData)} />}
                    style={getColumnStyles(room.id, {
                      maxWidth: '100px',
                      width: '100px',
                      textAlign: 'left',
                      textWrap: 'wrap',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="bpmProjected"
                    header="BPM Projected"
                    body={rowData =>
                      rowData.bpmProjected ? commaSeparatedNumbers(rowData.bpmProjected) : '-'
                    }
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="bpmOverride"
                    header="BPM Override"
                    body={getEditableColumnTemplate('bpmOverride', room.id, false, null, 'number')}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="cleanProjected"
                    header="Clean Projected"
                    body={rowData =>
                      rowData.cleanProjected ? commaSeparatedNumbers(rowData.cleanProjected) : '-'
                    }
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  <Column
                    field="cleanOverride"
                    header="Clean override"
                    body={getEditableColumnTemplate(
                      'cleanOverride',
                      room.id,
                      cleanTypeIsLoading,
                      clearnOverrideOptions,
                      'dropdown'
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  {/* Modified to only allow first row editing */}
                  <Column
                    field="numberOfBottlesInAtShiftStart"
                    header="Number of Bottles in (Shift Start)"
                    body={getEditableColumnTemplate(
                      'numberOfBottlesInAtShiftStart',
                      room.id,
                      false,
                      null,
                      'number',
                      true // Only first row editable
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                  {/* Modified to only allow first row editing */}
                  <Column
                    field="jobStartTime"
                    header="Job Start Time"
                    body={getEditableColumnTemplate(
                      'jobStartTime',
                      room.id,
                      false,
                      null,
                      'dateTime',
                      true, // Only first row editable
                      true,
                      false
                    )}
                    style={getColumnStyles(room.id, {
                      maxWidth: '50px',
                      width: '50px',
                      wordWrap: 'break-word'
                    })}
                  />
                </DataTable>
              </AccordionTab>
            );
          })}
      </Accordion>
    </div>
  );
};

export default observer(RoomAccordion);
